import * as XLSX from "xlsx";

// Lectura de archivos de cartera (Excel, CSV, ODS). Cada copropiedad los exporta
// desde un software contable distinto, así que aquí se reconocen títulos, fechas,
// montos y direcciones escritos de muchas formas.

const NIT_EXCLUIDO = "900087656";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

export const normalizarTexto = (t) =>
  String(t ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Acepta "$ 1.119.566,00", "1,119,566.00", "$-", "(5.000)", números de Excel, etc.
export const convertirNumero = (valor) => {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;
  const texto = String(valor ?? "").trim();
  const negativo = /^\(.*\d.*\)$/.test(texto) || /-\s*\$?\s*\d/.test(texto);
  let s = texto.replace(/[^\d.,]/g, "");
  if (!/\d/.test(s)) return 0;
  const punto = s.lastIndexOf(".");
  const coma = s.lastIndexOf(",");
  if (punto >= 0 && coma >= 0) {
    const decimal = punto > coma ? "." : ",";
    const miles = decimal === "." ? "," : ".";
    s = s.split(miles).join("").replace(decimal, ".");
  } else if (punto >= 0 || coma >= 0) {
    const sep = punto >= 0 ? "." : ",";
    const partes = s.split(sep);
    const sonMiles = partes.length > 2 || (partes[1].length === 3 && partes[0].length <= 3 && partes[0] !== "0");
    s = sonMiles ? partes.join("") : partes.join(".");
  }
  const n = Number(s) || 0;
  return negativo ? -n : n;
};

const pad = (n) => String(n).padStart(2, "0");

const fechaISO = (a, m, d) => {
  const f = new Date(Date.UTC(a, m - 1, d));
  if (a < 1990 || f.getUTCMonth() !== m - 1 || f.getUTCDate() !== d) return null;
  return `${a}-${pad(m)}-${pad(d)}`;
};

const ultimoDiaDelMes = (a, m) => new Date(Date.UTC(a, m, 0)).getUTCDate();

// Devuelve "aaaa-mm-dd" o null. Entiende "30/09/2026", "2026-09-30",
// "Septiembre 20 de 2026", "20 de septiembre de 2026", "Sep 2026" y fechas de Excel.
export const extraerFecha = (valor, aceptarNumeroExcel = false) => {
  if (typeof valor === "number") {
    if (!aceptarNumeroExcel || valor <= 0) return null;
    const f = XLSX.SSF.parse_date_code(valor);
    return f ? fechaISO(f.y, f.m, f.d) : null;
  }
  const s = String(valor ?? "");
  let m = s.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return fechaISO(+m[1], +m[2], +m[3]);
  m = s.match(/(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
  if (m) return fechaISO(+m[3], +m[2], +m[1]);
  m = s.match(/\b(\d{1,2})[-/.](\d{1,2})[-/.](\d{2})\b/);
  if (m) return fechaISO(2000 + +m[3], +m[2], +m[1]);

  const t = normalizarTexto(s);
  const mes = `(${MESES.join("|")})[a-z]*`;
  const numMes = (txt) => MESES.indexOf(txt.slice(0, 3)) + 1;
  m = t.match(new RegExp(`\\b(\\d{1,2}) (?:de )?${mes} (?:de |del )?(\\d{4})\\b`));
  if (m) return fechaISO(+m[3], numMes(m[2]), +m[1]);
  m = t.match(new RegExp(`\\b${mes} (\\d{1,2}) (?:de |del )?(\\d{4})\\b`));
  if (m) return fechaISO(+m[3], numMes(m[1]), +m[2]);
  m = t.match(new RegExp(`\\b${mes} (?:de |del )?(\\d{4})\\b`));
  if (m) return fechaISO(+m[2], numMes(m[1]), ultimoDiaDelMes(+m[2], numMes(m[1])));
  return null;
};

// ---------- Direcciones ----------

const ABREVIATURAS = {
  MZ: "MANZANA", MZA: "MANZANA", MZN: "MANZANA", MANZ: "MANZANA",
  CS: "CASA",
  LC: "LOCAL", LOC: "LOCAL", LOCALES: "LOCAL",
  APTO: "APARTAMENTO", APT: "APARTAMENTO", AP: "APARTAMENTO", APART: "APARTAMENTO", APARTAMENTOS: "APARTAMENTO",
  TO: "TORRE", TR: "TORRE", TRR: "TORRE",
  BL: "TORRE", BLQ: "TORRE", BLOQUE: "TORRE", INT: "TORRE", INTERIOR: "TORRE", EDIFICIO: "TORRE", ED: "TORRE",
};

const sinCeros = (v) => (/^\d+$/.test(v) ? String(parseInt(v, 10)) : v);

// Códigos como "L01" o "L-3": pueden ser locales o viviendas de la manzana/torre L; lo decide el usuario.
export const esCodigoL = (valor) => /^L[\s-]*\d+$/i.test(String(valor ?? "").trim());

// Convierte lo que venga en el archivo al formato de dirección de CoordiPH:
// "TORRE 1 APARTAMENTO 302", "MANZANA C CASA 2", "CASA 15" o "LOCAL 1".
// codigosL: "local" convierte "L01" en LOCAL 1; "vivienda" lo trata como cualquier otro código.
export const convertirDireccion = (valor, formato = "torre_apartamento", codigosL = "vivienda") => {
  const original = String(valor ?? "").trim();
  const tokens = original
    .normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase()
    .replace(/([A-Z])(\d)/g, "$1 $2")
    .replace(/(\d)([A-Z]{2,})/g, "$1 $2")
    .replace(/[^A-Z0-9]+/g, " ").trim()
    .split(" ").filter(Boolean)
    .map(t => ABREVIATURAS[t] || t);
  // "T2 APTO 302": la T sola es torre solo si también dice apartamento (en casas, "T02" es la manzana T).
  if (tokens.includes("APARTAMENTO")) tokens.forEach((t, i) => { if (t === "T" && /^\d+$/.test(tokens[i + 1] || "")) tokens[i] = "TORRE"; });
  const texto = tokens.join(" ");

  const despues = (palabra) => {
    const i = tokens.indexOf(palabra);
    return i >= 0 && tokens[i + 1] ? sinCeros(tokens[i + 1]) : null;
  };
  const manzana = despues("MANZANA");
  const torre = despues("TORRE");
  const casa = despues("CASA");
  const local = despues("LOCAL");
  const apto = despues("APARTAMENTO");
  // Número suelto después de la manzana o torre: "MANZANA C 2", "TORRE 1 302".
  const siguienteNumero = (palabra) => {
    const i = tokens.indexOf(palabra);
    return i >= 0 && /^\d+$/.test(tokens[i + 2] || "") ? sinCeros(tokens[i + 2]) : null;
  };

  if (codigosL === "local" && esCodigoL(original)) return `LOCAL ${sinCeros(original.replace(/\D/g, ""))}`;
  if (local && /^\d+$/.test(local)) return `LOCAL ${local}`;
  if (casa) return manzana ? `MANZANA ${manzana} CASA ${casa}` : `CASA ${casa}`;
  if (apto) return `TORRE ${torre || "1"} APARTAMENTO ${apto}`;
  if (manzana && siguienteNumero("MANZANA")) return `MANZANA ${manzana} CASA ${siguienteNumero("MANZANA")}`;
  if (torre && siguienteNumero("TORRE")) return `TORRE ${torre} APARTAMENTO ${siguienteNumero("TORRE")}`;

  const [a, b, c] = tokens;
  if (formato === "manzana_casa") {
    if (tokens.length === 2 && /^\d+$/.test(b)) return `MANZANA ${sinCeros(a)} CASA ${sinCeros(b)}`;
    if (tokens.length === 1 && /^\d+$/.test(a)) return `CASA ${sinCeros(a)}`;
    return texto;
  }
  if (tokens.length === 2 && /^\d+$/.test(a) && /^\d+$/.test(b)) return `TORRE ${sinCeros(a)} APARTAMENTO ${sinCeros(b)}`;
  if (tokens.length === 3 && a === "T" && /^\d+$/.test(b) && /^\d+$/.test(c)) return `TORRE ${sinCeros(b)} APARTAMENTO ${sinCeros(c)}`;
  if (original.length >= 3) return `TORRE 1 APARTAMENTO ${original}`;
  return original;
};

export const idDireccion = (direccion) => direccion.toLowerCase().replace(/\s+/g, "");

// ---------- Columnas ----------

// Conceptos de cartera en el orden en que se muestran; total_cartera va aparte.
export const CONCEPTOS_CARTERA = ["administracion", "retroactivo", "interes_mora", "seguro_areas_privadas", "multas_sanciones"];
const CONCEPTOS = [...CONCEPTOS_CARTERA, "total_cartera"];

// El orden importa: la primera regla que coincide decide el campo.
const REGLAS_COLUMNAS = [
  ["fecha",                 /\b(fecha|corte)\b/],
  ["meses_prom",            /\b(meses?|edad)\b/],
  ["retroactivo",           /\bretroactiv/],
  ["interes_mora",          /\binteres/],
  ["seguro_areas_privadas", /\b(seguros?|polizas?)\b/],
  ["multas_sanciones",      /\b(multas?|sancion|sanciones|inasistencia)\b/],
  ["administracion",        /\b(admon|adm|admin[a-z]*|expensas?|cuotas? ordinarias?)\b/],
  ["total_cartera",         /\b(total|totales|saldo|saldos|deuda|cartera)\b/],
  ["sector",                /^(torre|bloque|interior|manzana|mz|mza|etapa|edificio|bl)$/],
  ["numero",                /^(apartamento|apto|apt|ap|casa|numero|no|nro|num|lote|local|oficina)$/],
  ["direccion",             /\b(tercero|direccion|inmueble|unidad|predio|vivienda|nomenclatura|codigo|cod|referencia|ubicacion|apartamento|apto|casa)\b/],
];

// Entre varias columnas posibles de dirección se prefiere la del inmueble a la del tercero o el código.
const PRIORIDAD_DIRECCION = [/\b(inmueble|direccion|unidad|predio|vivienda|nomenclatura|ubicacion|apartamento|apto|casa)\b/, /\btercero\b/, /./];

const clasificarTitulo = (celda) => {
  const t = normalizarTexto(celda);
  if (!t) return null;
  const regla = REGLAS_COLUMNAS.find(([, re]) => re.test(t));
  return regla ? regla[0] : null;
};

const buscarEncabezado = (filas) => {
  for (let i = 0; i < Math.min(filas.length, 40); i++) {
    const tipos = filas[i].map(clasificarTitulo);
    const tieneDireccion = tipos.some(t => ["direccion", "numero"].includes(t));
    const tieneMonto = tipos.some(t => CONCEPTOS.includes(t));
    if (tieneDireccion && tieneMonto) return { indice: i, tipos };
  }
  return null;
};

const tieneDigito = (v) => /\d/.test(String(v ?? ""));
const esFilaDeTotales = (v) => /^(total|totales|subtotal|sub total|gran total|suma|sumas)\b/.test(normalizarTexto(v));

// ---------- Lectura ----------

const leerHoja = (hoja, formato, nit, codigosL) => {
  const filas = XLSX.utils.sheet_to_json(hoja, { header: 1, defval: "", blankrows: false });
  const encabezado = buscarEncabezado(filas);
  if (!encabezado) return null;
  const { indice, tipos } = encabezado;
  const titulos = filas[indice];
  const datos = filas.slice(indice + 1);

  const columnas = {};
  tipos.forEach((tipo, i) => {
    if (tipo && tipo !== "direccion" && tipo !== "numero" && columnas[tipo] === undefined) columnas[tipo] = i;
  });

  const candidatas = tipos.map((t, i) => (t === "direccion" || t === "numero" ? i : -1)).filter(i => i >= 0);
  const numeroSolo = tipos.findIndex(t => t === "numero");
  const usarSectorYNumero = columnas.sector !== undefined && numeroSolo >= 0;
  let colDireccion = -1;
  if (!usarSectorYNumero) {
    const rango = (i) => PRIORIDAD_DIRECCION.findIndex(re => re.test(normalizarTexto(titulos[i])));
    const conDigitos = (i) => {
      const valores = datos.map(f => f[i]).filter(v => String(v).trim());
      return valores.length > 0 && valores.filter(tieneDigito).length / valores.length >= 0.5;
    };
    const ordenadas = [...candidatas].sort((x, y) => rango(x) - rango(y) || x - y);
    colDireccion = ordenadas.find(conDigitos) ?? ordenadas[0];
  }
  const esManzana = usarSectorYNumero &&
    (/^(manzana|mz|mza)$/.test(normalizarTexto(titulos[columnas.sector])) || /^(casa|lote)$/.test(normalizarTexto(titulos[numeroSolo])));

  const usadas = new Set([...Object.values(columnas), colDireccion, numeroSolo]);
  const ignoradas = titulos
    .map((t, i) => ({ t: String(t).replace(/\s+/g, " ").trim(), i }))
    .filter(({ t, i }) => t && !usadas.has(i) && datos.some(f => convertirNumero(f[i]) !== 0))
    .map(({ t }) => t);

  const fechaTitulo = filas.slice(0, indice).flat().map(c => extraerFecha(c)).find(Boolean) || null;

  const registros = [];
  const omitidas = [];
  for (const fila of datos) {
    const valorDireccion = usarSectorYNumero
      ? (String(fila[columnas.sector]).trim() && String(fila[numeroSolo]).trim()
          ? `${esManzana ? "MANZANA" : "TORRE"} ${fila[columnas.sector]} ${esManzana ? "CASA" : "APARTAMENTO"} ${fila[numeroSolo]}`
          : "")
      : String(fila[colDireccion] ?? "").trim();
    const montos = Object.fromEntries(CONCEPTOS.map(c => [c, columnas[c] !== undefined ? Math.round(convertirNumero(fila[columnas[c]])) : 0]));
    const tieneMontos = CONCEPTOS.some(c => montos[c] !== 0);

    if (!valorDireccion || esFilaDeTotales(valorDireccion)) continue;
    if (!tieneDigito(valorDireccion) || valorDireccion.includes(NIT_EXCLUIDO) || (nit && valorDireccion.includes(nit))) {
      if (tieneMontos) omitidas.push(valorDireccion);
      continue;
    }

    const direccion = convertirDireccion(valorDireccion, esManzana ? "manzana_casa" : formato, codigosL);
    const sumaConceptos = CONCEPTOS_CARTERA.reduce((suma, c) => suma + montos[c], 0);
    registros.push({
      original: valorDireccion,
      id: idDireccion(direccion),
      tercero: direccion,
      ...Object.fromEntries(CONCEPTOS_CARTERA.map(c => [c, montos[c]])),
      total_cartera: columnas.total_cartera !== undefined ? montos.total_cartera : sumaConceptos,
      meses_prom: columnas.meses_prom !== undefined ? convertirNumero(fila[columnas.meses_prom]) : 0,
      fechaFila: columnas.fecha !== undefined ? extraerFecha(fila[columnas.fecha], true) : null,
    });
  }
  return { registros, omitidas, ignoradas, fechaTitulo };
};

const sumarRegistro = (a, b) => ({
  ...a,
  ...Object.fromEntries(CONCEPTOS.map(c => [c, a[c] + b[c]])),
  meses_prom: Math.max(a.meses_prom, b.meses_prom),
});

// Lee todas las hojas del libro. Si una hoja repite exactamente los mismos inmuebles
// de otra (hoja resumen o copia), se ignora; si un inmueble aparece varias veces
// en la misma hoja, sus valores se suman.
// formato: "manzana_casa" o "torre_apartamento", según lo que elija el usuario al importar.
// codigosL: "local" o "vivienda" (ver convertirDireccion). El resultado trae codigosConL para poder preguntar.
export const leerLibroCartera = (libro, { formato = "torre_apartamento", codigosL = "vivienda", nit = "", nombreArchivo = "" } = {}) => {
  const hoy = new Date().toISOString().slice(0, 10);
  const fechaArchivo = extraerFecha(nombreArchivo);
  const datos = {};
  const ejemplos = [];
  const omitidas = [];
  const ignoradas = new Set();
  const codigosConL = [];
  let fechaCorte = null;
  let encontroEncabezado = false;

  for (const nombreHoja of libro.SheetNames) {
    const hoja = leerHoja(libro.Sheets[nombreHoja], formato, String(nit), codigosL);
    if (!hoja) continue;
    encontroEncabezado = true;
    const idsHoja = new Set(hoja.registros.map(r => r.id));
    if (idsHoja.size === 0 || [...idsHoja].every(id => datos[id])) continue;

    const fechaHoja = hoja.fechaTitulo || fechaArchivo;
    fechaCorte = fechaCorte || fechaHoja || hoja.registros.map(r => r.fechaFila).find(Boolean) || null;
    const deEstaHoja = {};
    for (const { original, id, fechaFila, ...registro } of hoja.registros) {
      const fila = { ...registro, fecha: fechaFila || fechaHoja || hoy };
      deEstaHoja[id] = deEstaHoja[id] ? sumarRegistro(deEstaHoja[id], fila) : fila;
      const codigoL = esCodigoL(original);
      if (codigoL) codigosConL.push(original);
      // Se muestran hasta 4 ejemplos normales y hasta 2 códigos con L, para que se vea cómo quedan.
      const mostrados = ejemplos.filter(e => esCodigoL(e.original) === codigoL).length;
      if (mostrados < (codigoL ? 2 : 4) && !ejemplos.some(e => e.direccion === registro.tercero)) {
        ejemplos.push({ original, direccion: registro.tercero });
      }
    }
    Object.assign(datos, deEstaHoja);
    omitidas.push(...hoja.omitidas);
    hoja.ignoradas.forEach(t => ignoradas.add(t));
  }

  return {
    encontroEncabezado,
    datos,
    ejemplos,
    omitidas,
    columnasIgnoradas: [...ignoradas],
    codigosConL,
    fechaCorte: fechaCorte || hoy,
    fechaEncontrada: Boolean(fechaCorte),
  };
};

const decodificarTexto = (buffer) => {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("windows-1252").decode(buffer);
  }
};

export const leerArchivoCartera = async (archivo, opciones = {}) => {
  const buffer = await archivo.arrayBuffer();
  const esTexto = /\.(csv|txt|tsv)$/i.test(archivo.name);
  const libro = esTexto
    ? XLSX.read(decodificarTexto(buffer), { type: "string", raw: true })
    : XLSX.read(buffer, { type: "array" });
  return leerLibroCartera(libro, { ...opciones, nombreArchivo: archivo.name });
};
